<template>
  <!-- Drug<->diagnosis FDA-approval verdicts, one row per medication on the order.
       Reads order.drugIndicationChecks, prefetched by the order-detail serializer (no extra API call). -->
  <div v-if="checks.length" class="drug-indication mt-4">
    <div class="d-flex align-center ga-2 mb-2">
      <v-icon size="20" color="primary">mdi-pill</v-icon>
      <span class="text-subtitle-1 font-weight-bold">Drug–Diagnosis FDA Approval Check</span>
    </div>
    <div class="text-body-2 text-medium-emphasis mb-3">
      FDA-approval status of each medication against the order's diagnoses. Informational only — it does not change the
      submission.
    </div>

    <v-table density="comfortable" class="drug-indication__table">
      <thead>
        <tr>
          <th class="text-left">Medication</th>
          <th class="text-left">Diagnosis</th>
          <th class="text-left">FDA approval</th>
          <th class="text-left">Notes</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(check, index) in checks" :key="index">
          <td class="py-2">
            <div class="font-weight-medium">{{ check.medication || "—" }}</div>
            <div v-if="check.ingredientRxcui" class="text-caption text-medium-emphasis">
              RxCUI {{ check.ingredientRxcui }}
            </div>
          </td>
          <td class="py-2">{{ check.icd10Code || "—" }}</td>
          <td class="py-2">
            <div class="d-flex flex-column ga-1 align-start">
              <v-chip :color="verdictDisplay(check.verdict).color" size="small" variant="flat" label>
                <v-icon start size="small">{{ verdictDisplay(check.verdict).icon }}</v-icon>
                {{ verdictDisplay(check.verdict).label }}
              </v-chip>
              <v-chip v-if="check.doNotUse" color="error" size="x-small" variant="tonal" label>
                <v-icon start size="x-small">mdi-alert-octagon</v-icon>
                Contraindication / Boxed warning
              </v-chip>
            </div>
          </td>
          <td class="py-2 text-body-2">{{ check.reason || "—" }}</td>
        </tr>
      </tbody>
    </v-table>
  </div>
</template>

<script setup>
import { computed } from "vue";

const props = defineProps({
  order: {
    type: Object,
    required: true,
  },
});

const checks = computed(() => props.order.drugIndicationChecks || []);

// Map each verdict to a chip color, user-facing label, and icon.
// `unverified` must read as "not checked", NOT as "not approved".
const VERDICT_DISPLAY = {
  on_label: { color: "success", label: "On-label", icon: "mdi-check-circle" },
  off_label: { color: "warning", label: "Off-label", icon: "mdi-information" },
  not_indicated: { color: "error", label: "Not indicated", icon: "mdi-alert-circle" },
  unverified: { color: "grey", label: "Not checked", icon: "mdi-help-circle" },
};

const verdictDisplay = (verdict) => VERDICT_DISPLAY[verdict] || VERDICT_DISPLAY.unverified;
</script>

<style scoped>
.drug-indication__table {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  overflow: hidden;
}
</style>
